import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import tenantWebSocketService from '../services/tenantWebSocketService';
import userWebSocketService from '../services/userWebSocketService';
import websocketService from '../services/websocketService';

const WebSocketContext = createContext();

export const useWebSockets = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSockets must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { user, currentTenant } = useAuth();
  const tenantId = currentTenant?.id || user?.tenant_id;
  const userId = user?.id;
  
  // Refs to track connection states
  const tenantConnectedRef = useRef(false);
  const userConnectedRef = useRef(false);
  const connectionAttemptsRef = useRef({ tenant: 0, user: 0 });
  const maxConnectionAttempts = 3;

  // Connect to Tenant WebSocket
  const connectTenantWebSocket = async () => {
    if (!tenantId || !userId) {
      ////console.log('🏢 Missing tenant ID or user ID for Tenant WebSocket connection');
      return;
    }

    if (tenantConnectedRef.current) {
      //console.log('🏢 Tenant WebSocket already connected');
      return;
    }

    try {
      //console.log('🏢 Connecting to Tenant WebSocket...', { tenantId, userId });
      await tenantWebSocketService.connect(tenantId, userId);
      tenantConnectedRef.current = true;
      connectionAttemptsRef.current.tenant = 0;
      //console.log('✅ Tenant WebSocket connected successfully');
    } catch (error) {
      console.error('❌ Failed to connect Tenant WebSocket:', error);
      connectionAttemptsRef.current.tenant++;
      
      if (connectionAttemptsRef.current.tenant < maxConnectionAttempts) {
        //console.log(`🔄 Retrying Tenant WebSocket connection (${connectionAttemptsRef.current.tenant}/${maxConnectionAttempts})...`);
        setTimeout(connectTenantWebSocket, 2000);
      } else {
        console.error('❌ Max Tenant WebSocket connection attempts reached');
      }
    }
  };

  // Connect to User WebSocket
  const connectUserWebSocket = async () => {
    if (!userId) {
      //console.log('👤 Missing user ID for User WebSocket connection');
      return;
    }

    if (userConnectedRef.current) {
      //console.log('👤 User WebSocket already connected');
      return;
    }

    try {
      await userWebSocketService.connect(userId);
      userConnectedRef.current = true;
      connectionAttemptsRef.current.user = 0;
    } catch (error) {
      console.error('❌ Failed to connect User WebSocket:', error);
      connectionAttemptsRef.current.user++;
      
      if (connectionAttemptsRef.current.user < maxConnectionAttempts) {
        //console.log(`🔄 Retrying User WebSocket connection (${connectionAttemptsRef.current.user}/${maxConnectionAttempts})...`);
        setTimeout(connectUserWebSocket, 2000);
      } else {
        console.error('❌ Max User WebSocket connection attempts reached');
      }
    }
  };

  // Connect both WebSockets when user logs in with delay
  const connectWebSockets = async () => {
    if (!user || !tenantId) {
      //console.log('🔌 Missing user or tenant data for WebSocket connections');
      return;
    }

    //console.log('🔌 CONNECTING WEBSOCKETS ON LOGIN');

    // Add delay to prevent overwhelming the browser
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Connect WebSockets sequentially instead of parallel for better browser compatibility
    try {
      await connectTenantWebSocket();
      // Small delay between connections
      await new Promise(resolve => setTimeout(resolve, 500));
      await connectUserWebSocket();
    } catch (error) {
      console.error('❌ WebSocket connection error:', error);
    }
  };

  // Disconnect both WebSockets when user logs out
  const disconnectWebSockets = () => {
    tenantWebSocketService.disconnect();
    userWebSocketService.disconnect();
    
    tenantConnectedRef.current = false;
    userConnectedRef.current = false;
    connectionAttemptsRef.current = { tenant: 0, user: 0 };
    
    //console.log('✅ All WebSockets disconnected');
  };

  // Handle user login - connect WebSockets
  useEffect(() => {
    if (user && tenantId && !tenantConnectedRef.current && !userConnectedRef.current) {
      console.log('🔌 User logged in, connecting WebSockets...', { user: !!user, tenantId, tenantConnected: tenantConnectedRef.current, userConnected: userConnectedRef.current });
      connectWebSockets();
    }
  }, [user, tenantId]);

  // Handle user logout - disconnect WebSockets
  useEffect(() => {
    if (!user) {
      //console.log('🔌 User logged out, disconnecting WebSockets...');
      disconnectWebSockets();
    }
  }, [user]);

  // Handle tenant change - reconnect WebSockets
  useEffect(() => {
    if (user && tenantId) {
      //console.log('🔌 Tenant changed, reconnecting WebSockets...');
      disconnectWebSockets();
      setTimeout(() => {
        connectWebSockets();
      }, 1000);
    }
  }, [tenantId]);

  // Manual reconnect function
  const reconnectWebSockets = async () => {
    //console.log('🔄 Manual WebSocket reconnection triggered');
    disconnectWebSockets();
    setTimeout(() => {
      connectWebSockets();
    }, 1000);
  };

  // Get connection status
  const getConnectionStatus = () => {
    return {
      tenant: {
        connected: tenantConnectedRef.current,
        status: tenantWebSocketService.getConnectionStatus(),
        isConnected: tenantWebSocketService.isConnected()
      },
      user: {
        connected: userConnectedRef.current,
        status: userWebSocketService.getConnectionStatus(),
        isConnected: userWebSocketService.isConnected()
      }
    };
  };

  // Handle stale connection events
  useEffect(() => {
    const handleStaleConnection = (service) => {
      console.warn(`⚠️ ${service} WebSocket connection detected as stale, attempting reconnection...`);
      // You can add UI notifications here if needed
    };

    // Listen for stale connection events from all three WebSocket services
    tenantWebSocketService.on('connecting', (data) => {
      if (data.reason === 'stale_connection') {
        handleStaleConnection('Tenant');
      }
    });

    userWebSocketService.on('connecting', (data) => {
      if (data.reason === 'stale_connection') {
        handleStaleConnection('User');
      }
    });

    websocketService.on('connecting', (data) => {
      if (data.reason === 'stale_connection') {
        handleStaleConnection('Chat');
      }
    });

    return () => {
      // Cleanup listeners if needed
    };
  }, []);

  const value = {
    // Connection status
    getConnectionStatus,
    
    // Manual actions
    reconnectWebSockets,
    connectWebSockets,
    disconnectWebSockets,
    
    // Services (for direct access if needed)
    tenantWebSocketService,
    userWebSocketService,
    websocketService
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;
